/**Packages */
import moment from "moment";

/**Helpers */
import { MENU_DATE_FORMAT } from "./constants";
import { getMenuRules, getCurrentUser } from "./state";

/**
 * Menu rules engine, ported from mysodexo-pwa (www/js/filter-menu.js and functions/utils/menu_filters.js).
 *
 * A menu rule (document canteens/{cid}/rules/menu, array "filters") looks like:
 * {
 *   occasion?, diet?, component?, recipe?: string   -> what it targets (case-insensitive regex on every language of entity.name, AND between keys)
 *   is_filter_enabled?: boolean|null                 -> null/undefined = no date check (see checkFilterOnOff)
 *   start_date?, end_date?: Timestamp ({seconds})
 *   time?: "HH:mm", offset?: number, before?: boolean|null
 *   week_days?: number (bitmask of today, Mon = bit0), target_week_days?: number (bitmask of the menu date)
 *   tags?: string[]                                  -> user must share at least one tag
 *   services?: string[]                              -> never applies in someal (no current service)
 *   show?, orderable?, faded?, hide_price?: boolean, message?: string
 * }
 * The last matching rule wins. In someal only "show" has a visible effect, the other outcomes are
 * computed and attached to the recipe (filter_*) for future use.
 */

const FILTER_KEYS = ["occasion", "diet", "component", "recipe"];

const DEFAULT_FILTER_STATUS = {
    show: true,
    orderable: true,
    message: null,
    faded: true,
    hide_price: false,
    timeOut: null,
};

/**
 *
 * @param {*} entity - occasion / diet / course / recipe
 * @returns {Array<string>} all the names (every language) of the entity
 */
const getEntityNames = entity => {
    const name = entity?.name;

    if (!name) return [];
    if (typeof name === "string") return [name];
    if (typeof name !== "object") return [];

    return Object.values(name).filter(value => typeof value === "string");
};

/**
 *
 * @param {*} filter - menu rule
 * @param {string} menuDate - date of the menu (MENU_DATE_FORMAT)
 * @returns true if the rule is active for the menu date, otherwise false
 */
export const checkFilterOnOff = (filter, menuDate) => {
    if (!filter) return false;

    if (filter.is_filter_enabled === null || filter.is_filter_enabled === undefined) {
        return true;
    }

    const menuDateObj = moment(menuDate, MENU_DATE_FORMAT);
    const startDate = filter.start_date?.seconds != null ? moment.unix(filter.start_date.seconds) : null;
    const endDate = filter.end_date?.seconds != null ? moment.unix(filter.end_date.seconds) : null;

    let isInRange = menuDateObj.isBetween(startDate, endDate, null, "[]");

    // If startDate and endDate are both null, disable the filter
    if (startDate === null && endDate === null) {
        isInRange = false;
    }

    // If startDate equals endDate the range is that single day
    if (startDate && endDate && startDate.isSame(endDate)) {
        isInRange = menuDateObj.isSame(startDate, "day");
    }

    // Only startDate: from then on
    if (startDate !== null && !endDate) {
        isInRange = menuDateObj.isSameOrAfter(startDate);
    }

    // Only endDate: until then
    if (!startDate && endDate && menuDateObj.isSameOrBefore(endDate)) {
        isInRange = true;
    } else if (!startDate && endDate) {
        isInRange = false;
    }

    // Same (inverted) semantic of mysodexo-pwa: is_filter_enabled = true -> active OUTSIDE the range
    return filter.is_filter_enabled ? !isInRange : isInRange;
};

/**
 *
 * @param {*} filter - menu rule
 * @param {*} entities - { occasion, diet, component, recipe }
 * @returns true if every key defined in the rule matches the related entity, otherwise false
 */
export const checkFilter = (filter, entities = {}) => {
    let totalFilters = 0,
        respected = 0;

    FILTER_KEYS.forEach(key => {
        if (filter[key] !== undefined) {
            totalFilters++;

            if (filter[key]) {
                // Value is not escaped, as in mysodexo-pwa; an invalid regex is treated as "no match"
                // instead of throwing, so a bad rule can't blank the whole menu
                let regex = null;
                try {
                    regex = new RegExp(`.*${filter[key]}.*`, "i");
                } catch (error) {
                    regex = null;
                }

                if (regex && getEntityNames(entities[key]).some(name => regex.test(name))) {
                    respected++;
                }
            } else {
                // If the filter is defined but empty, consider it a match
                respected++;
            }
        }
        // If a filter is undefined, do not count it in totalFilters, effectively treating it as a match
    });

    return respected === totalFilters;
};

/**
 *
 * @returns { filterStatus, timeOut } - filterStatus true if the rule applies now (time, days, tags, services)
 */
export const filterApply = ({
    time = "00:00", // The cutoff time as a string in "HH:mm" format
    offset = 0, // Number of days to offset the cutoff time
    before, // true = applies before the cutoff, false = after, null/undefined = always
    week_days = 0b1111111, // Bitmask for days when the user is viewing the menu (Mon = bit0)
    target_week_days = 0b1111111, // Bitmask for days of the menu date (Mon = bit0)
    tags = [], // Tags to check against user's tags
    menuDate = moment().format(MENU_DATE_FORMAT),
    userTags = [],
    services = null,
    currentService = null, // Always null in someal: a rule with services never applies
    now = moment(),
}) => {
    const menuDateMoment = moment(menuDate, MENU_DATE_FORMAT).startOf("day");

    // dayOfWeek = when the user is viewing; menuDayOfWeek = selected menu day
    const dayOfWeek = (now.day() + 6) % 7;
    const menuDayOfWeek = (menuDateMoment.day() + 6) % 7;

    const returnObj = { filterStatus: false, timeOut: null };

    let hasCommonTag = true;
    if (Array.isArray(tags) && tags.length > 0) {
        hasCommonTag = Array.isArray(userTags) ? userTags.some(tag => tags.includes(tag)) : false;
    }

    if (
        !(week_days & (1 << dayOfWeek)) ||
        !(target_week_days & (1 << menuDayOfWeek)) ||
        !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time) ||
        !hasCommonTag
    ) {
        return returnObj;
    }

    const [hours, minutes] = time.split(":").map(Number);

    // Cutoff is based on the menu date, not today
    const cutOff = moment(menuDate, MENU_DATE_FORMAT)
        .startOf("day")
        .add(offset, "days")
        .set({ hour: hours, minute: minutes })
        .endOf("minute");

    returnObj.timeOut = now.diff(cutOff, "seconds");
    returnObj.filterStatus = before == null ? true : before ? returnObj.timeOut <= 0 : returnObj.timeOut >= 0;

    if (services && Array.isArray(services) && services.length > 0) {
        returnObj.filterStatus = services.some(service => service === currentService);
    }

    return returnObj;
};

/**
 *
 * @param {*} entities - { recipe, course, diet, occasion } (raw menu objects, with "name")
 * @param {string} menuDate - date of the menu (MENU_DATE_FORMAT)
 * @param {Array} filters - menu rules (default: from Redux)
 * @param {Array} userTags - current user tags (default: from Redux)
 * @param {*} now - moment of "now" (injectable for tests)
 * @returns { show, orderable, message, faded, hide_price, timeOut }
 */
export const getRecipeFilterStatus = (
    { recipe = null, course = null, diet = null, occasion = null } = {},
    menuDate = moment().format(MENU_DATE_FORMAT),
    filters = getMenuRules(),
    userTags = getCurrentUser()?.tags,
    now = moment(),
) => {
    const status = { ...DEFAULT_FILTER_STATUS };

    // Rules not loaded yet / reducer initial state ({}) / null
    if (!Array.isArray(filters) || filters.length === 0) return status;

    const entities = { recipe, component: course, diet, occasion };

    for (let idx = 0; idx < filters.length; idx++) {
        const filter = filters[idx];

        if (!filter) continue;

        if (checkFilterOnOff(filter, menuDate)) {
            const { filterStatus, timeOut } = filterApply({ ...filter, menuDate, userTags, now });
            status.timeOut = timeOut;

            if (filterStatus && checkFilter(filter, entities)) {
                //The last matching rule wins
                status.show = filter.show ?? false;
                status.orderable = filter.orderable ?? true;
                status.message = filter.message ?? status.message;
                status.faded = filter.faded ?? true;
                status.hide_price = filter.hide_price ?? false;
            }
        }
    }

    return status;
};

/**
 *
 * @returns true if the recipe is to hide, otherwise false
 */
export const isMenuEntityHidden = (...args) => {
    return !getRecipeFilterStatus(...args).show;
};

/**
 *
 * @param {*} menu - raw menu (occasions -> diets -> courses -> recipes)
 * @param {string} menuDate - date of the menu (MENU_DATE_FORMAT)
 * @param {Array} filters - menu rules (default: from Redux)
 * @param {Array} userTags - current user tags (default: from Redux)
 * @param {*} now - moment of "now" (injectable for tests)
 * @returns A new menu without the hidden recipes (and without empty courses / diets / occasions).
 * The input is never mutated (the raw menu is cached in Redux).
 */
export const getMenuFilteredByRules = (
    menu,
    menuDate = moment().format(MENU_DATE_FORMAT),
    filters = getMenuRules(),
    userTags = getCurrentUser()?.tags,
    now = moment(),
) => {
    if (!menu || !Array.isArray(menu.occasions)) return menu;
    if (!Array.isArray(filters) || filters.length === 0) return menu;

    const occasions = menu.occasions
        .map(occasion => {
            const diets = (occasion.diets || [])
                .map(diet => {
                    const courses = (diet.courses || [])
                        .map(course => {
                            const recipes = (course.recipes || []).reduce((visibleRecipes, recipe) => {
                                const status = getRecipeFilterStatus(
                                    { recipe, course, diet, occasion },
                                    menuDate,
                                    filters,
                                    userTags,
                                    now,
                                );

                                if (status.show) {
                                    visibleRecipes.push({
                                        ...recipe,
                                        filter_orderable: status.orderable,
                                        filter_faded: status.faded,
                                        filter_hide_price: status.hide_price,
                                        filter_message: status.message,
                                    });
                                }

                                return visibleRecipes;
                            }, []);

                            return { ...course, recipes };
                        })
                        .filter(course => course.recipes.length > 0);

                    return { ...diet, courses };
                })
                .filter(diet => diet.courses.length > 0);

            return { ...occasion, diets };
        })
        .filter(occasion => occasion.diets.length > 0);

    return { ...menu, occasions };
};
